import t from 'react-translate';
import uuid from 'react-uuid';
import moment from 'moment';
import { Dispatch,
  MouseEventHandler,
  SetStateAction,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';

// redux
import { wrapThunkAction } from 'redux/utils';
import { getAiRolePlayConversation } from 'redux/actions/ai-role-play';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getAiRolePlay, getAiRolePlaySubmission } from 'redux/selectors/timeline';

import NvTooltip from 'shared/components/nv-tooltip';
import { config } from '../../../../config/pendo.config.json';
import { LecturePageMode } from '..';
import {
  AIRolePlayStatus,
  Message,
  MessageStatus,
  SessionData,
} from './ai-conversation-modal/context';

type SubmitSectionProps = {
  aiRolePlayId: number;
  handleOpenRolePlay: MouseEventHandler<HTMLElement>;
  mode: LecturePageMode;
  messages: Message[];
  setMessages: Function;
  setAiRolePlayStatus: Function;
  setShowConversationModal: Function;
  isHardDeadlineExpired: boolean;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  setOpeningNarrative: Dispatch<SetStateAction<string>>;
};

const SubmitSection = (props: SubmitSectionProps) => {
  const {
    aiRolePlayId,
    handleOpenRolePlay,
    mode,
    messages,
    setMessages,
    setAiRolePlayStatus,
    setShowConversationModal,
    isHardDeadlineExpired,
    setSessionData,
    setOpeningNarrative,
  } = props;
  const dispatch = useDispatch();
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));
  const submission = useSelector((state) => getAiRolePlaySubmission(state, aiRolePlay?.submissionId));
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);

  const [isFetchingConversation, setIsFetchingConversation] = useState(false);

  const handleViewFeedback = () => {
    setSessionData((prevSession) => ({
      ...prevSession,
      submissionId: submission?.id || null,
    }));
    if (messages.length > 0) {
      setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);
      setShowConversationModal(true);
      return;
    }
    setIsFetchingConversation(true);
    wrapThunkAction(dispatch(getAiRolePlayConversation(
      {
        submissionId: submission.id,
        catalogId: currentCatalogId,
      },
    ))).then((res) => {
      const { chatMessages } = res.payload as { chatMessages: Message[] };
      setOpeningNarrative(chatMessages[0]?.content);
      const conversation = chatMessages.slice(1).map((message) => ({
        ...message,
        status: MessageStatus.SENT,
        id: uuid(),
      }));
      setMessages(conversation);
      setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);
      setShowConversationModal(true);
    }).catch((err) => {
      dispatch(
        addAlertMessage({
          header: t.FORM.SOMETHING_WRONG(),
          type: AlertMessageType.ERROR,
        }),
      );
    }).finally(() => {
      setIsFetchingConversation(false);
    });
  };

  if (submission?.id) {
    return (
      <div className='d-flex  flex-column mt-6'>
        {submission?.completedAt && (
          <p
            className='text-gray-3 font-weight-bold text-small'
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.LAST_COMPLETED_ON(moment(submission.completedAt).format('LLL'))}
          </p>
        )}
        <div className='d-flex justify-content-center'>
          <NvTooltip
            text={t.EVALUATIONS.DEADLINE_PASSED()}
            enabled={isHardDeadlineExpired && mode === LecturePageMode.VIEW}
          >
            <Button
              variant='secondary'
              className='mr-1'
              disabled={isFetchingConversation || mode !== LecturePageMode.VIEW || isHardDeadlineExpired}
              onClick={handleOpenRolePlay}
              data-qa={config.pendo.activities.aiRolePlay.retry}
            >
              {t.FORM.RETRY()}
            </Button>
          </NvTooltip>
          <Button
            variant='primary'
            className='ml-1'
            disabled={isFetchingConversation || mode !== LecturePageMode.VIEW}
            onClick={handleViewFeedback}
            data-qa={config.pendo.activities.aiRolePlay.viewFeedback}
          >
            {t.EVALUATIONS.VIEW_FEEDBACK()}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='d-flex flex-column mt-6'>
      {isHardDeadlineExpired && (
        <p className='text-gray-3 font-weight-bold text-small'>
          {t.EVALUATIONS.DEADLINE_PASSED()}
        </p>
      )}
      <div className='d-flex justify-content-center'>
        <Button
          variant='primary'
          disabled={mode !== LecturePageMode.VIEW || isHardDeadlineExpired}
          onClick={handleOpenRolePlay}
          data-qa={config.pendo.activities.aiRolePlay.startRolePlay}
        >
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.START_AI_ROLE_PLAY()}
        </Button>
      </div>
    </div>
  );
};

export default SubmitSection;
